import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import classesService from '../services/classesService';
import campusService from '../services/campusService';
import { Plus, X, CircleNotch } from '@phosphor-icons/react';

function ClassesPage() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [campuses, setCampuses] = useState([]);
  const [campusFilter, setCampusFilter] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    level: 'primary_1',
    section: '',
    capacity: 40,
    room_number: '',
    campus_id: ''
  });

  const classLevels = [
    { value: 'kg1', label: 'KG 1' },
    { value: 'kg2', label: 'KG 2' },
    { value: 'primary_1', label: 'Primary 1' },
    { value: 'primary_2', label: 'Primary 2' },
    { value: 'primary_3', label: 'Primary 3' },
    { value: 'primary_4', label: 'Primary 4' },
    { value: 'primary_5', label: 'Primary 5' },
    { value: 'primary_6', label: 'Primary 6' },
    { value: 'jhs_1', label: 'JHS 1' },
    { value: 'jhs_2', label: 'JHS 2' },
    { value: 'jhs_3', label: 'JHS 3' },
  ];

  const loadClasses = () => {
    setLoading(true);
    classesService.listClasses({ campus_id: campusFilter || undefined })
      .then(data => setClasses(data || []))
      .catch(err => toast.error('Failed to load classes'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadClasses();
  }, [campusFilter]);

  useEffect(() => {
    campusService.listCampuses()
      .then(data => setCampuses(Array.isArray(data) ? data : []))
      .catch(err => console.error('Failed to load campuses:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await classesService.createClass(formData);
      toast.success('Class created successfully!');
      setShowModal(false);
      setFormData({
        name: '',
        level: 'primary_1',
        section: '',
        capacity: 40,
        room_number: '',
        campus_id: ''
      });
      loadClasses();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="page" data-testid="classes-page">
        <div className="page-header">
          <div>
            <h1>Classes</h1>
            <p>Manage class sections and enrollment</p>
          </div>
          <div className="header-actions">
            {campuses.length > 0 && (
              <select
                value={campusFilter}
                onChange={(e) => setCampusFilter(e.target.value)}
                data-testid="classes-campus-filter"
                style={{ marginRight: 8 }}
              >
                <option value="">All Campuses</option>
                {campuses.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
            <button className="btn btn-primary" onClick={() => setShowModal(true)} data-testid="add-class-btn">
              <Plus size={18} weight="bold" /> Add Class
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading classes...</div>
        ) : (
          <div className="classes-grid">
            {classes.map(cls => (
              <div key={cls.id} className="class-card" data-testid={`class-card-${cls.id}`}>
                <div className="class-header">
                  <h3>{cls.name}</h3>
                  <span className={`badge ${cls.is_active ? 'active' : 'inactive'}`}>
                    {cls.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="class-details">
                  <div className="detail-row">
                    <span>Level:</span>
                    <span className="capitalize">{cls.level?.replace('_', ' ')}</span>
                  </div>
                  <div className="detail-row">
                    <span>Students:</span>
                    <span>{cls.student_count || 0} / {cls.capacity}</span>
                  </div>
                  {cls.room_number && (
                    <div className="detail-row">
                      <span>Room:</span>
                      <span>{cls.room_number}</span>
                    </div>
                  )}
                </div>
                <div className="class-actions">
                  <Link to={`/classes/${cls.id}`} className="btn btn-sm btn-primary">View Details</Link>
                </div>
              </div>
            ))}
            {classes.length === 0 && (
              <div className="empty-state-card">
                <p>No classes created yet</p>
                <button className="btn btn-primary" onClick={() => setShowModal(true)}>Create First Class</button>
              </div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} data-testid="add-class-modal">
              <div className="modal-header">
                <h2>Create New Class</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-group">
                  <label>Class Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Primary 1A"
                    disabled={submitting}
                    required
                    data-testid="class-name-input"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Level</label>
                    <select
                      value={formData.level}
                      onChange={(e) => setFormData({...formData, level: e.target.value})}
                      disabled={submitting}
                      data-testid="class-level-select"
                    >
                      {classLevels.map(level => (
                        <option key={level.value} value={level.value}>{level.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Section</label>
                    <input
                      type="text"
                      value={formData.section}
                      onChange={(e) => setFormData({...formData, section: e.target.value})}
                      placeholder="e.g., A, B, C"
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Capacity</label>
                    <input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})}
                      disabled={submitting}
                      min="1"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label>Room Number</label>
                    <input
                      type="text"
                      value={formData.room_number}
                      onChange={(e) => setFormData({...formData, room_number: e.target.value})}
                      placeholder="e.g., Room 101"
                      disabled={submitting}
                    />
                  </div>
                </div>
                {campuses.length > 0 && (
                  <div className="form-group">
                    <label>Campus</label>
                    <select
                      value={formData.campus_id}
                      onChange={(e) => setFormData({...formData, campus_id: e.target.value})}
                      disabled={submitting}
                      data-testid="class-campus-select"
                    >
                      <option value="">No campus</option>
                      {campuses.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</button>
                  <button type="submit" className="btn btn-primary" data-testid="save-class-btn">
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Creating...
                      </span>
                    ) : (
                      'Create Class'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ClassesPage;
